import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '../../contexts/AuthContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Card from '../ui/Card'
import Button from '../ui/Button'
import Switch from '../ui/Switch'
import Badge from '../ui/Badge'
import GamificationSettings from '../settings/GamificationSettings'

const { FiZap, FiTrendingUp, FiTarget, FiSettings, FiStar, FiClock, FiUsers, FiSun, FiCalendar, FiAward } = FiIcons

const GamificationWidget = () => {
  const { family } = useAuth()
  const [showFullSettings, setShowFullSettings] = useState(false)
  
  const gamificationSettings = family?.gamification_settings || {}
  const enhancedPointsEnabled = gamificationSettings.enhanced_points?.enabled || false
  const pointMultipliersEnabled = gamificationSettings.point_multipliers?.enabled || false
  
  // Check what features are currently active
  const getActiveFeatures = () => {
    const active = []
    
    if (enhancedPointsEnabled) {
      const enhanced = gamificationSettings.enhanced_points
      if (enhanced.quality_bonus?.enabled) active.push('Quality Bonus')
      if (enhanced.speed_bonus?.enabled) active.push('Speed Bonus')
      if (enhanced.helper_bonus?.enabled) active.push('Helper Bonus')
    }
    
    if (pointMultipliersEnabled) {
      const multipliers = gamificationSettings.point_multipliers
      if (multipliers.happy_hour?.enabled) active.push('Happy Hour')
      if (multipliers.weekend_warriors?.enabled) active.push('Weekend Warriors')
      if (multipliers.challenge_week?.enabled) active.push('Challenge Week')
    }
    
    return active
  }

  const activeFeatures = getActiveFeatures()
  const isAnyFeatureActive = enhancedPointsEnabled || pointMultipliersEnabled

  // Check if it's currently happy hour
  const isHappyHourActive = () => {
    if (!pointMultipliersEnabled || !gamificationSettings.point_multipliers?.happy_hour?.enabled) {
      return false
    }
    
    const now = new Date()
    const currentTime = now.toTimeString().slice(0, 5)
    const currentDay = now.getDay()
    const happyHour = gamificationSettings.point_multipliers.happy_hour
    
    return happyHour.days?.includes(currentDay) && 
           currentTime >= happyHour.start_time && 
           currentTime <= happyHour.end_time
  }

  // Check if it's weekend and weekend warriors is active
  const isWeekendWarriorsActive = () => {
    if (!pointMultipliersEnabled || !gamificationSettings.point_multipliers?.weekend_warriors?.enabled) {
      return false
    }
    
    const currentDay = new Date().getDay()
    return [0, 6].includes(currentDay) // Sunday or Saturday
  }

  // Check if challenge week is active
  const isChallengeWeekActive = () => {
    if (!pointMultipliersEnabled || !gamificationSettings.point_multipliers?.challenge_week?.enabled) {
      return false
    }
    
    const challengeWeek = gamificationSettings.point_multipliers.challenge_week
    if (!challengeWeek.start_date || !challengeWeek.end_date) return false
    
    const today = new Date().toISOString().split('T')[0]
    return today >= challengeWeek.start_date && today <= challengeWeek.end_date
  }

  const formatTime = (time) => {
    if (!time) return ''
    const [hours, minutes] = time.split(':')
    const hour = parseInt(hours)
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour % 12 || 12
    return `${displayHour}:${minutes} ${ampm}`
  }

  if (showFullSettings) {
    return (
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Gamification Settings</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowFullSettings(false)}
            className="text-gray-500"
          >
            ← Back to Widget
          </Button>
        </div>
        <GamificationSettings />
      </Card>
    )
  }

  return (
    <Card className={`transition-all duration-300 ${isAnyFeatureActive ? 'bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 border-primary-200 dark:border-primary-700' : ''}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${isAnyFeatureActive ? 'bg-primary-500 text-white' : 'bg-gray-100 dark:bg-gray-800'}`}>
            <SafeIcon icon={FiZap} className="text-lg" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Gamification
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {isAnyFeatureActive ? 'Enhanced points active!' : 'Boost motivation with bonus points'}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowFullSettings(true)}
          className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
        >
          <SafeIcon icon={FiSettings} />
        </Button>
      </div>

      {/* Status Overview */}
      <div className="space-y-4">
        {/* Quick Toggle */}
        <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiTrendingUp} className="text-green-500" />
            <div>
              <span className="font-medium text-gray-900 dark:text-gray-100">Enhanced Points</span>
              <p className="text-xs text-gray-600 dark:text-gray-400">Quality, speed & helper bonuses</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {enhancedPointsEnabled && <Badge variant="success" size="sm">Active</Badge>}
            <Switch
              checked={enhancedPointsEnabled}
              onChange={() => setShowFullSettings(true)}
              size="sm"
            />
          </div>
        </div>

        <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiTarget} className="text-orange-500" />
            <div>
              <span className="font-medium text-gray-900 dark:text-gray-100">Point Multipliers</span>
              <p className="text-xs text-gray-600 dark:text-gray-400">Happy hour, weekends & challenges</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {pointMultipliersEnabled && <Badge variant="info" size="sm">Active</Badge>}
            <Switch
              checked={pointMultipliersEnabled}
              onChange={() => setShowFullSettings(true)}
              size="sm"
            />
          </div>
        </div>

        {/* Active Features */}
        {isAnyFeatureActive && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="space-y-3"
          >
            <div className="flex items-center gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
              <SafeIcon icon={FiStar} className="text-yellow-500" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Currently Active:</span>
            </div>

            {/* Live Status Indicators */}
            <div className="space-y-2">
              {isHappyHourActive() && (
                <motion.div
                  initial={{ scale: 0.95 }}
                  animate={{ scale: 1 }}
                  className="flex items-center gap-2 p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg"
                >
                  <SafeIcon icon={FiSun} className="text-yellow-600 dark:text-yellow-400" />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                      🔥 Happy Hour Active!
                    </span>
                    <p className="text-xs text-yellow-700 dark:text-yellow-300">
                      {gamificationSettings.point_multipliers.happy_hour.multiplier}x points until {formatTime(gamificationSettings.point_multipliers.happy_hour.end_time)}
                    </p>
                  </div>
                  <Badge variant="warning" size="sm">LIVE</Badge>
                </motion.div>
              )}

              {isWeekendWarriorsActive() && (
                <motion.div
                  initial={{ scale: 0.95 }}
                  animate={{ scale: 1 }}
                  className="flex items-center gap-2 p-2 bg-green-100 dark:bg-green-900/30 rounded-lg"
                >
                  <SafeIcon icon={FiCalendar} className="text-green-600 dark:text-green-400" />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-green-800 dark:text-green-200">
                      🏆 Weekend Warriors!
                    </span>
                    <p className="text-xs text-green-700 dark:text-green-300">
                      {gamificationSettings.point_multipliers.weekend_warriors.multiplier}x points for outdoor tasks
                    </p>
                  </div>
                  <Badge variant="success" size="sm">LIVE</Badge>
                </motion.div>
              )}

              {isChallengeWeekActive() && (
                <motion.div
                  initial={{ scale: 0.95 }}
                  animate={{ scale: 1 }}
                  className="flex items-center gap-2 p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg"
                >
                  <SafeIcon icon={FiAward} className="text-purple-600 dark:text-purple-400" />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-purple-800 dark:text-purple-200">
                      🎯 {gamificationSettings.point_multipliers.challenge_week.theme}
                    </span>
                    <p className="text-xs text-purple-700 dark:text-purple-300">
                      {gamificationSettings.point_multipliers.challenge_week.multiplier}x points this week!
                    </p>
                  </div>
                  <Badge variant="primary" size="sm">LIVE</Badge>
                </motion.div>
              )}

              {/* Enhanced Points Features */}
              {enhancedPointsEnabled && (
                <div className="grid grid-cols-3 gap-2">
                  {gamificationSettings.enhanced_points?.quality_bonus?.enabled && (
                    <div className="flex items-center gap-1 p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-center">
                      <SafeIcon icon={FiStar} className="text-blue-500 text-xs" />
                      <span className="text-xs text-blue-700 dark:text-blue-300">Quality</span>
                    </div>
                  )}
                  {gamificationSettings.enhanced_points?.speed_bonus?.enabled && (
                    <div className="flex items-center gap-1 p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-center">
                      <SafeIcon icon={FiClock} className="text-blue-500 text-xs" />
                      <span className="text-xs text-blue-700 dark:text-blue-300">Speed</span>
                    </div>
                  )}
                  {gamificationSettings.enhanced_points?.helper_bonus?.enabled && (
                    <div className="flex items-center gap-1 p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-center">
                      <SafeIcon icon={FiUsers} className="text-blue-500 text-xs" />
                      <span className="text-xs text-blue-700 dark:text-blue-300">Helper</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Call to Action */}
        {!isAnyFeatureActive && (
          <div className="text-center py-4">
            <div className="text-2xl mb-2">🎮</div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Make chores more exciting with bonus points and multipliers!
            </p>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowFullSettings(true)}
              className="flex items-center gap-2"
            >
              <SafeIcon icon={FiZap} />
              Enable Gamification
            </Button>
          </div>
        )}

        {/* Quick Actions */}
        {isAnyFeatureActive && (
          <div className="flex gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFullSettings(true)}
              className="flex-1 text-xs"
            >
              <SafeIcon icon={FiSettings} className="text-xs" />
              Customize
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFullSettings(true)}
              className="flex-1 text-xs"
            >
              View All Settings
            </Button>
          </div>
        )}
      </div>
    </Card>
  )
}

export default GamificationWidget